import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/lib/i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

import { ROUTE_PATHS } from "@/lib/index";
import { useAuth } from "@/hooks/useAuth";
import { useVersionCheck } from "@/hooks/useVersionCheck";

// Import security protection (blocks dev tools)
import '@/lib/security';

import RootPage from "@/pages/RootPage";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import UserSystem from "@/pages/UserSystem";
import MatchBetting from "@/pages/MatchBetting";
import MatchManagement from "@/pages/MatchManagement";
import MatchResults from "@/pages/MatchResults";
import Reports from "@/pages/Reports";
import WalletBalance from "@/pages/WalletBalance";
import PreMatchSummary from "@/pages/PreMatchSummary";
import PreMatchSummary2 from "@/pages/PreMatchSummary2";
import CreditManagement from "@/pages/CreditManagement";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable caching - always fetch fresh data
      staleTime: 0,
      gcTime: 0, // Garbage collection time (formerly cacheTime)
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

/**
 * A wrapper component that redirects unauthenticated users to the login page.
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  console.log('ProtectedRoute check:', { isAuthenticated, isLoading, user: !!user });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to login');
    return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
  }

  console.log('Authenticated, rendering protected content');
  return <>{children}</>;
};

/**
 * Root application component with routing and global providers.
 * ProjectViva - © 2026
 */
const App = () => {
  // Check for version updates
  useVersionCheck();
  
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
      <TooltipProvider>
        <HashRouter>
          <Routes>
            {/* Root page with smart redirect */}
            <Route path="/" element={<RootPage />} />
            
            {/* Public Routes */}
            <Route path={ROUTE_PATHS.LOGIN} element={<Login />} />

            {/* Protected Dashboard Route */}
            <Route
              path={ROUTE_PATHS.DASHBOARD}
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Protected Feature Routes */}
            <Route
              path={ROUTE_PATHS.USERS}
              element={
                <ProtectedRoute>
                  <UserSystem />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTE_PATHS.MATCHES}
              element={
                <ProtectedRoute>
                  <MatchBetting />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTE_PATHS.REPORTS}
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTE_PATHS.WALLET}
              element={
                <ProtectedRoute>
                  <WalletBalance />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTE_PATHS.PRE_MATCH_SUMMARY}
              element={
                <ProtectedRoute>
                  <PreMatchSummary />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTE_PATHS.PRE_MATCH_SUMMARY_2}
              element={
                <ProtectedRoute>
                  <PreMatchSummary2 />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTE_PATHS.MATCH_MANAGEMENT}
              element={
                <ProtectedRoute>
                  <MatchManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTE_PATHS.CREDIT}
              element={
                <ProtectedRoute>
                  <CreditManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTE_PATHS.MATCH_RESULTS}
              element={
                <ProtectedRoute>
                  <MatchResults />
                </ProtectedRoute>
              }
            />

            {/* Catch-all Redirect - Protected — default to Match Betting */}
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <Navigate to={ROUTE_PATHS.MATCHES} replace />
                </ProtectedRoute>
              }
            />
          </Routes>
        </HashRouter>
        <Toaster />
        <Sonner position="top-right" expand={false} richColors />
      </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;